import { z } from "zod";
import { formatShare } from "#app/widgets/definitions/shared/format-share";
import {
  Box,
  ChartLegend,
  ProportionBar,
  WidgetEmpty,
  WidgetSkeleton,
  useWidgetInstance,
  type ProportionBarSegment,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { groupBySchema } from "#app/widgets/dashboards/use-dimension-counts";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import {
  useShapedSlices,
  sliceDrilldownIds,
  MISSING_FIELD_DESCRIPTION,
  MISSING_FIELD_TITLE,
  sliceDataFields,
} from "#app/widgets/dashboards/use-dimension-slices";
import {
  calculateSchema,
  measureSchema,
  withLegacyDataKeys,
} from "#app/widgets/dashboards/widget-measure";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { workloadTitle } from "../shared/derived-titles";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";

/**
 * `dimension-battery` — a single 100%-stacked horizontal proportion bar (a
 * "battery"): projects grouped by any dimension, each category a rounded segment
 * whose width is its share of the whole, on the `ProportionBar` finished chart.
 * Each segment wears its entity's OWN color via `sliceColors.tokens` (the
 * `ChartLegend` items read the same assignment, so segments and legend always
 * line up). Reads the shared `useShapedSlices` plumbing and Show / Sort
 * vocabulary; the tail folds into an "Other" segment so the segments always sum
 * to the whole. A share is a share of a whole, so the battery plots counts and
 * sums; under an average or an extreme Measure the segments still size by
 * figure but print no percentages.
 */
export const dimensionBatteryConfigSchema = withLegacyDataKeys(
  z.looseObject({
    measure: measureSchema,
    calculate: calculateSchema,
    groupBy: groupBySchema.default("stage"),
    show: z.enum(["top5", "top8", "top12", "all"]).default("top8"),
    sort: z.enum(["volume", "alpha"]).default("volume"),
    percentages: z.boolean().default(true),
    legend: z.boolean().default(true),
    filters: widgetFiltersSchema.nullish(),
  }),
);

export type DimensionBatteryConfig = z.infer<typeof dimensionBatteryConfigSchema>;

function DimensionBatteryRender({
  w,
  h,
  expanded,
  config,
}: WidgetRenderContext<DimensionBatteryConfig>) {
  const { data, slices, sliceColors, dimension, measure, pending, loading, error } = useShapedSlices({
    groupBy: config.groupBy,
    measure: config.measure,
    calculate: config.calculate,
    sort: config.sort,
    show: config.show,
    filters: config.filters,
  });
  // Click-through: a segment press opens this card's Data drawer on its rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  const emptyTitle = useWindowEmptyTitle("No Projects", config.filters);

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="battery" />;
  }
  if (slices.length === 0) {
    return pending ? (
      <WidgetEmpty title={MISSING_FIELD_TITLE} description={MISSING_FIELD_DESCRIPTION} />
    ) : (
      <WidgetEmpty title={emptyTitle} />
    );
  }

  const total = slices.reduce((sum, d) => sum + d.total, 0);
  const additive = !measure.measure || measure.measure.fn === "sum";
  const percentages = config.percentages && additive;
  const share = (value: number) => formatShare(value, total);
  // Medium cards spend their body on the bar itself — segment hover names each
  // share, and entity-owned colors carry the identity the rest of the app
  // already taught. A legend earns its rows only where there is room to be a
  // compact data table: the expanded preview, or a genuinely large card.
  const largeCard = !expanded && w >= 8 && h >= 5;
  const showLegend = config.legend && (expanded || largeCard);

  const segments = slices.map((s) => ({ id: s.id, label: s.label, value: s.total }));

  const handleSegmentClick =
    drill && instance && dimension
      ? (segment: ProportionBarSegment) => {
          const s = slices.find((sl) => sl.id === segment.id);
          if (!s) return;
          drill.openData(instance.instanceId, {
            kind: "dimension",
            dimension,
            ids: sliceDrilldownIds(s, data, slices),
            label: s.label,
          });
        }
      : undefined;

  return (
    <Box direction="column" fill="flex" gap="sm" justify="center">
      <ProportionBar
        segments={segments}
        colorTokens={sliceColors.tokens}
        // Identity lives on the bar whenever the legend is folded away: wide
        // segments name themselves, slivers stay quiet behind their hover card.
        showLabels={!showLegend}
        onSegmentClick={handleSegmentClick}
        ariaLabel={measure.measure ? `${measure.label} split` : "Workload split"}
        renderTooltip={(segment) => {
          const slice = slices.find((s) => s.id === segment.id);
          const count = slice?.count ?? segment.value;
          return (
            <WidgetChartTooltip
              label={segment.label}
              details={`${measure.detail(segment.value, count)}${percentages ? ` · ${share(segment.value)}` : ""}`}
            />
          );
        }}
      />
      {showLegend ? (
        <ChartLegend
          items={slices.map((s, i) => ({
            label: s.label,
            colorToken: sliceColors.tokens[i],
            value: percentages
              ? `${measure.compact(s.total)} · ${share(s.total)}`
              : measure.compact(s.total),
          }))}
          justify="center"
        />
      ) : null}
    </Box>
  );
}

const dimensionBatterySettings = (config: DimensionBatteryConfig): WidgetSettingsGroup[] => [
  {
    title: "Source",
    fields: [{ key: "source", kind: "source", label: "Workflow" }],
  },
  {
    title: "Data",
    fields: sliceDataFields(config),
  },
  {
    title: "Display",
    fields: [
      { key: "percentages", kind: "toggle", label: "Display as percentages", icon: "percent" },
      { key: "legend", kind: "toggle", label: "Show legend", icon: "list" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const dimensionBatteryDef: WidgetModule<DimensionBatteryConfig> = {
  type: "dimension-battery",
  title: "Battery chart",
  dynamicTitle: (config, context) => workloadTitle(config, config.groupBy, "Battery chart", context),
  icon: "percent",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 5 },
  configSchema: dimensionBatteryConfigSchema,
  settings: dimensionBatterySettings,
  headerActions: WidgetHeaderFilters,
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "dimension-battery.custom",
      label: "Battery chart",
      description: "Show the share of Projects, or of a field total, across a dimension as one stacked bar.",
      section: "custom" satisfies WidgetSection,
      // Lands named by its gallery label. The label is not the family's while
      // every choice is built in, so it sticks like a rename until the user
      // renames the card or brings a custom field in — then the title derives
      // from the field ("Deductible by Bid Type").
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "stage" },
      defaultW: 5,
    },
    {
      key: "dimension-battery.outcome",
      label: "Outcome share",
      description: "The share of Project Updates recorded under each outcome, as one battery bar.",
      section: "outcomes" satisfies WidgetSection,
      tags: ["activity", "outcomes"] satisfies readonly WidgetTag[],
      icon: PROPERTY_ICON.followUp,
      defaultConfig: { groupBy: "outcome" },
      locks: ["groupBy"],
      defaultW: 5,
    },
    {
      key: "dimension-battery.age",
      label: "Workload by follow up",
      description:
        "Projects whose follow up is On track, Update soon or Stale, as one bar.",
      section: "projects" satisfies WidgetSection,
      tags: ["projects", "ageHealth"] satisfies readonly WidgetTag[],
      icon: PROPERTY_ICON.followUp,
      // Label matches the derived form — bake nothing, let `dynamicTitle` track.
      autoTitle: false,
      defaultConfig: { groupBy: "age" },
      locks: ["groupBy"],
      defaultW: 5,
    },
  ],
  render: DimensionBatteryRender,
};
